import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Polaroid } from '../components/ui/Polaroid';
import { InkUnderline, RevealText } from '../components/ui/RevealText';
import { MarketingNav, BackToTop } from '../components/ui/MarketingNav';
import { SiteFooter } from './AboutPage';

export default function HowItWorksPage() {
  return (
    <div className="min-h-screen">
      <MarketingNav />

      {/* ─── Hero / opening ─── */}
      <section className="container-page relative pb-3xl pt-lg">
        <div className="mx-auto max-w-4xl text-center">
          <p className="kicker justify-center">Cum funcționează</p>

          <RevealText
            as="h1"
            className="mt-md font-serif leading-[0.95]"
            delay={0.1}
            stagger={0.04}
          >
            {`Din momentul în care spui „da" `}
          </RevealText>
          <h1
            className="font-serif leading-[0.95]"
            style={{
              fontSize: 'clamp(3rem, 6vw, 5.4rem)',
              fontVariationSettings: "'opsz' 144, 'SOFT' 100, 'WONK' 1",
            }}
          >
            <span className="text-gradient italic">până la cartea tipărită.</span>
          </h1>

          <p className="mt-lg max-w-2xl mx-auto text-lg text-fg-soft">
            Tot ce se întâmplă între tine, invitații tăi și noi —{' '}
            <InkUnderline soft>cronologic, sincer, fără ascunzători</InkUnderline>.
            Fără pași pe care nu-i înțelegi, fără termeni tehnici pe care
            trebuie să-i cauți pe Google.
          </p>
        </div>

        <div className="relative mx-auto mt-3xl max-w-md">
          <Polaroid
            src="https://images.unsplash.com/photo-1583939003579-730e3918a45a?w=900&q=80"
            alt="Un telefon cu un cod QR scanat"
            caption="✦ un singur tap"
            rotate={-2}
            aspect="4/5"
          />
        </div>
      </section>

      {/* ─── Big chronological timeline ─── */}
      <section className="bg-bg-alt py-4xl">
        <div className="container-page paper-grain relative">
          <div className="mx-auto max-w-2xl text-center">
            <p className="kicker justify-center">Cronologia completă</p>
            <h2 className="mt-sm" style={{ fontVariationSettings: "'opsz' 144" }}>
              De la „vreau să fac nuntă" la cartea de pe masă.
            </h2>
          </div>

          <ol className="mx-auto mt-3xl max-w-2xl space-y-3xl">
            {TIMELINE.map((m, i) => (
              <motion.li
                key={m.when}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: '-60px' }}
                transition={{ delay: i * 0.08, duration: 0.7 }}
                className="grid grid-cols-1 gap-lg lg:grid-cols-12"
              >
                <div className="lg:col-span-3">
                  <p
                    className="font-serif text-accent"
                    style={{
                      fontSize: 'clamp(2.2rem, 4vw, 3.2rem)',
                      lineHeight: 0.9,
                      fontVariationSettings: "'opsz' 144",
                    }}
                  >
                    {m.when}
                  </p>
                </div>
                <div className="border-l-2 border-accent/40 pl-lg lg:col-span-9">
                  <h3
                    className="font-serif"
                    style={{
                      fontSize: 'clamp(1.6rem, 2.6vw, 2.2rem)',
                      lineHeight: 1.1,
                      fontVariationSettings: "'opsz' 144",
                    }}
                  >
                    {m.title}
                  </h3>
                  <p className="mt-md max-w-xl text-lg text-fg-soft">
                    {m.body}
                  </p>
                </div>
              </motion.li>
            ))}
          </ol>
        </div>
      </section>

      {/* ─── For each role: what happens for them ─── */}
      <section className="container-page py-4xl">
        <div className="mx-auto max-w-2xl text-center">
          <p className="kicker justify-center">Pentru fiecare, altfel</p>
          <h2 className="mt-sm" style={{ fontVariationSettings: "'opsz' 144" }}>
            Ce face fiecare, concret.
          </h2>
        </div>

        <div className="mt-3xl grid grid-cols-1 gap-2xl lg:grid-cols-3">
          {ROLES.map((r, i) => (
            <motion.div
              key={r.role}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: '-50px' }}
              transition={{ delay: i * 0.1, duration: 0.7 }}
            >
              <Polaroid
                src={r.img}
                alt={r.role}
                tag={r.role}
                rotate={r.rotate}
                aspect="4/5"
                delay={i * 100}
              />
              <h3
                className="mt-md font-serif"
                style={{
                  fontSize: '1.6rem',
                  fontVariationSettings: "'opsz' 144",
                }}
              >
                {r.headline}
              </h3>
              <ul className="mt-md space-y-sm text-fg-soft">
                {r.steps.map((s, j) => (
                  <li key={j} className="flex gap-sm">
                    <span className="shrink-0 font-mono text-xs text-accent">
                      {String(j + 1).padStart(2, '0')}
                    </span>
                    <span className="text-sm leading-relaxed">{s}</span>
                  </li>
                ))}
              </ul>
            </motion.div>
          ))}
        </div>
      </section>

      {/* ─── What we promise not to do ─── */}
      <section className="bg-bg-alt py-4xl">
        <div className="container-page">
          <div className="grid grid-cols-1 gap-2xl lg:grid-cols-12">
            <div className="lg:col-span-5">
              <p className="kicker">Ce nu facem</p>
              <h2 className="mt-sm" style={{ fontVariationSettings: "'opsz' 144" }}>
                Și ce nu te obligăm
                <br />
                <em className="italic text-fg-soft">să faci tu.</em>
              </h2>
            </div>
            <div className="lg:col-span-7">
              <ul className="divide-y divide-border">
                {DONT.map(([t, b]) => (
                  <li key={t} className="py-md">
                    <p className="font-mono text-xs uppercase tracking-[0.2em] text-fg-soft">
                      ✕ {t}
                    </p>
                    <p className="mt-xs text-fg">{b}</p>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* ─── Pull quote band ─── */}
      <section className="border-y border-border bg-bg py-3xl">
        <div className="container-page">
          <blockquote className="mx-auto max-w-3xl text-center">
            <p
              className="font-serif italic"
              style={{ fontSize: 'clamp(1.6rem, 2.6vw, 2.4rem)', lineHeight: 1.3 }}
            >
              „Cel mai bun lucru pe care-l putem face e să dispărem.
              Să nu simți că există o platformă între tine și oamenii
              tăi."
            </p>
            <p className="mt-md font-mono text-xs uppercase tracking-[0.2em] text-fg-soft">
              — Notița de pe frigiderul din birou
            </p>
          </blockquote>
        </div>
      </section>

      {/* ─── FAQ specifică paginii ─── */}
      <section className="container-page py-4xl">
        <div className="mx-auto max-w-3xl">
          <div className="text-center">
            <p className="kicker justify-center">Întrebări practice</p>
            <h2 className="mt-sm" style={{ fontVariationSettings: "'opsz' 144" }}>
              Răspunsuri la lucruri pe care{' '}
              <span className="text-fg-soft">le-am auzit deja</span>.
            </h2>
          </div>

          <div className="mt-2xl space-y-md">
            {FAQ.map(([q, a]) => (
              <motion.div
                key={q}
                initial={{ opacity: 0, y: 10 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5 }}
                className="border-l-2 border-accent/40 pl-md py-sm"
              >
                <p className="font-serif text-lg">{q}</p>
                <p className="mt-sm text-fg-soft">{a}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* ─── CTA ─── */}
      <section className="container-page pb-4xl">
        <div className="mx-auto max-w-2xl text-center">
          <p className="kicker justify-center">Suficient despre cum funcționează</p>
          <h2 className="mt-sm font-serif" style={{ fontVariationSettings: "'opsz' 144" }}>
            Vrei să vezi <em className="italic text-fg-soft">cum arată</em>?
          </h2>
          <div className="mt-xl flex flex-wrap justify-center gap-sm">
            <Link to="/#preturi" className="btn-primary">
              Vezi prețurile
            </Link>
            <a href="/#demo-live" className="btn-ghost">
              Deschide o galerie demo →
            </a>
          </div>
        </div>
      </section>

      <SiteFooter />
      <BackToTop />
    </div>
  );
}

const TIMELINE = [
  {
    when: 'ziua -90',
    title: 'Îți faci contul și alegi evenimentul.',
    body: 'Îți trebuie 90 de secunde: mail, numele evenimentului, data. Noi generăm automat link-ul și QR-urile. Fără abonament, fără perioadă de probă, fără „card necesar pentru activare".',
  },
  {
    when: 'ziua -14',
    title: 'Tipărești QR-urile. Le pui pe mese.',
    body: 'Primești un PDF gata printabil, A4, cu câte un cod per masă, plus câteva loose pentru intrare și bar. Arată ca niște cartonașe, nu ca o reclamă. Tipărești tu, la orice print de cartier, pe hârtie normală.',
  },
  {
    when: 'ziua 0',
    title: 'Invitații scanează, încarcă, se bucură.',
    body: 'Camera telefonului se deschide pe pagina noastră, apare un buton mare „Încarcă o poză". Fără login. Fără aplicație. Funcționează pe orice telefon făcut după 2016. Pozele ajung la tine în 2-3 secunde, în coada de moderare.',
  },
  {
    when: 'ziua 0 / 23:00',
    title: 'Moderare în timp real, în pauză.',
    body: 'Între melodii sau când pleci la toaletă, deschizi telefonul și glisezi prin ultimele cadre. Cele care-ți plac — un tap. Cele care nu — swipe. Opțional: poți cere unui prieten de încredere să modereze în locul tău.',
  },
  {
    when: 'ziua +1',
    title: 'Galeria publică se deschide.',
    body: 'Trimi link-ul pe WhatsApp la toți invitații. Ei văd doar pozele aprobate, pot comenta și da like, se pot bucura de toată seara. Moderarea rămâne activă — poți adăuga sau șterge cadre oricând.',
  },
  {
    when: 'ziua +30',
    title: 'Primești PDF-ul de revistă.',
    body: 'După 30 de zile — perioada în care mai apar poze „întârziate" de la invitați care au uitat — îți trimitem un PDF printabil, format revistă, cu copertă personalizată, gata dus la orice tipografie. 80 lei / exemplar, opțional.',
  },
  {
    when: 'ziua +365',
    title: 'Ce se întâmplă după un an?',
    body: 'Îți dăm trei opțiuni: păstrăm încă un an (49 lei), le ștergem definitiv, sau ți le descărcăm pe un stick USB și le arhivăm noi în siguranță. Tu alegi. Noi nu decidem în locul tău.',
  },
];

const ROLES = [
  {
    role: 'Mireasa',
    headline: 'Tu controlezi ce văd alții.',
    img: 'https://images.unsplash.com/photo-1519741497674-611481863552?w=700&q=80',
    rotate: -2,
    steps: [
      'Deschizi galeria, glisezi printre cadre, apeși o dată pe cele care-ți plac.',
      'Restul rămân în seiful tău. Nimeni nu le vede, nici măcar noi.',
      'Comentezi la cadrele cu mătușa Maria în prim-plan.',
      'Descarci PDF-ul final, îl duci la tipografie, îl pui pe masa din sufragerie.',
    ],
  },
  {
    role: 'Invitat',
    headline: 'Nimic de instalat. Doar telefonul.',
    img: 'https://images.unsplash.com/photo-1606800052052-a08af7148866?w=700&q=80',
    rotate: 2,
    steps: [
      'Scanezi QR-ul de pe masă cu camera telefonului.',
      'Apară un buton mare: „Încarcă o poză". Alegi pozele, apeși trimite.',
      'Opțional: lași un comentariu, dai like la cadrele altora.',
      'Gata. Telefonul tău nu mai e deranjat de nimic.',
    ],
  },
  {
    role: 'Nașul',
    headline: 'Poți modera în locul mirilor.',
    img: 'https://images.unsplash.com/photo-1583939003579-730e3918a45a?w=700&q=80',
    rotate: -1.5,
    steps: [
      'Mirii îți dau acces. Primești un link cu drepturi de moderare.',
      'Vezi aceleași cadre ca ei, dar fără opțiunea de ștergere definitivă.',
      'Recomandarea noastră: ești „poarta de intrare" — triezi rapid, mirii decid finalul.',
    ],
  },
];

const DONT = [
  ['Nu-ți cerem cont cu parolă', 'Invitații tăi au deja 47 de conturi. Nu le mai dăm încă unul.'],
  ['Nu-ți cerem cardul în avans', 'Plătești doar când evenimentul e gata configurat, după ce ești sigur că vrei.'],
  ['Nu-ți punem reclame', 'Galeria ta e a ta. Niciun banner, niciun „articol recomandat", nimic.'],
  ['Nu-ți vindem datele', 'Pozele nunții tale nu ajung în niciun set de antrenament, niciodată.'],
  ['Nu te obligăm la aplicație', 'Totul se întâmplă pe web, în browser, pe orice telefon.'],
  ['Nu-ți blocăm fișierele', 'La final, descarci un ZIP cu toate pozele în original, fără watermark.'],
];

const FAQ = [
  ['Ce se întâmplă dacă un invitat are telefon vechi?', 'Funcționează pe orice telefon cu cameră și browser. Testăm pe iOS 13 și Android 8 — cam din 2016 încoace.'],
  ['Dar dacă nu am semnal la sală?', 'Invitații pot face poze și încărca când revin la semnal. Aplicația lor nu e necesară — pune un WiFi la sală sau un hotspot de la un telefon.'],
  ['Pot să adaug moderare automată?', 'Nu. E o decizie de produs. Pozele cu fețe închise sau cu mama miresei într-un unghi nasol — tu decizi. Nu un AI.'],
  ['Pot să am doi moderatori?', 'Da. Trimitem un link separat. Văd aceleași cadre, au drepturi de moderare.'],
  ['Se poate printa direct la o tipografie?', 'Da. PDF-ul e gândi pentru print A4, format revistă, cu bleed. Orice tipografie locală îl poate tipări.'],
  ['Cât timp rămân pozele online?', 'Cât vrei tu. Implicit 1 an inclus. Prelungire 49 lei/an. La cerere, le ștergem oricând.'],
];
